import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError, api, idempotencyKeyFor, releaseIdempotencyKey } from "../api/client.js";
import type {
  EventView,
  HintView,
  ParticipantView,
  QuestionView,
  ScoreView,
} from "../api/types.js";
import { ConfirmDialog } from "../components/ConfirmDialog.js";
import { Header } from "../components/Header.js";
import { LockedFinalCard } from "../components/LockedFinalCard.js";
import { QuestionCard } from "../components/QuestionCard.js";
import { ToastStack, type ToastData, type ToastKind } from "../components/Toast.js";
import type { AnswerSubmission } from "../components/AnswerForm.js";
import { activityMessage } from "../lib/activityMessage.js";
import { answerSignature, formatDelta } from "../lib/format.js";
import { useRealtime } from "../realtime/useRealtime.js";
import { FinishedScreen, PausedScreen, WaitingScreen } from "./SimpleScreens.js";
import type { ScoreUpdatePayload } from "../realtime/types.js";

interface Props {
  event: EventView;
  participant: ParticipantView;
  initialScore: ScoreView;
  onUnauthorized: () => void;
}

/**
 * WebSocket が切れている間の保険として、この間隔で得点を取得し直す。
 * 接続できている間はサーバーからの配信で更新されるため実行しない。
 */
const FALLBACK_POLL_INTERVAL_MS = 15_000;

/**
 * 「オフライン」を表示するまでの猶予。
 * 一瞬の切断で表示が点滅すると、かえって不安にさせるため。
 */
const OFFLINE_DISPLAY_DELAY_MS = 3_000;

/**
 * 同時に表示する通知の上限。
 *
 * 通知は4秒（エラーは6秒）で自動的に消えるが、チームの人数が多いと
 * 消える前に次が積み上がり、問題一覧を覆ってしまう。
 * 古いものから捨てて、画面下部を占有しすぎないようにする。
 */
const MAX_VISIBLE_TOASTS = 3;

/** 配信されたペイロードを画面の得点表現へ変換する */
function toScoreView(payload: ScoreUpdatePayload): ScoreView {
  return {
    rawScore: payload.rawScore,
    displayScore: payload.displayScore,
    solvedCount: payload.solvedCount,
    hintCount: payload.hintCount,
    wrongCount: payload.wrongCount,
    lastSolvedAt: payload.lastSolvedAt,
  };
}

export function PlayScreen({
  event: initialEvent,
  participant,
  initialScore,
  onUnauthorized,
}: Props): React.ReactElement {
  const [event, setEvent] = useState(initialEvent);
  const [score, setScore] = useState(initialScore);
  const [questions, setQuestions] = useState<QuestionView[] | null>(null);
  /** 未解放の大謎があるか。ロックカードの表示に使う（基本設計書 5.7） */
  const [finalLocked, setFinalLocked] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [submittingId, setSubmittingId] = useState<string | null>(null);
  const [openingHintId, setOpeningHintId] = useState<string | null>(null);
  const [pendingHint, setPendingHint] = useState<HintView | null>(null);
  /**
   * 最後の1回を使う直前に確認する。
   * 上限つきの問題で誤って送ってしまうと取り返しがつかないため、
   * 残り1回のときだけワンクッション置く。
   */
  const [pendingAnswer, setPendingAnswer] = useState<{
    question: QuestionView;
    submission: AnswerSubmission;
  } | null>(null);
  const [toasts, setToasts] = useState<ToastData[]>([]);

  /** 問題ごとのクールダウン終了時刻（エポックミリ秒） */
  const [cooldowns, setCooldowns] = useState<Record<string, number>>({});
  const [, forceTick] = useState(0);

  const toastSeq = useRef(0);
  const pushToast = useCallback((kind: ToastKind, text: string): void => {
    toastSeq.current += 1;
    const id = toastSeq.current;
    setToasts((prev) => [...prev, { id, kind, text }].slice(-MAX_VISIBLE_TOASTS));
  }, []);
  const dismissToast = useCallback((id: number): void => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  /** API エラーを画面表示へ変換する */
  const handleApiError = useCallback(
    (err: unknown, questionId?: string): void => {
      if (!(err instanceof ApiError)) {
        pushToast("error", "予期しないエラーが発生しました。");
        return;
      }
      if (err.isUnauthorized) {
        onUnauthorized();
        return;
      }
      // クールダウンの残り秒数はサーバーの値を採用する
      if (err.code === "E006" && questionId !== undefined) {
        const remaining = Number(err.params.n ?? 0);
        setCooldowns((prev) => ({ ...prev, [questionId]: Date.now() + remaining * 1000 }));
      }
      pushToast("error", err.message);
    },
    [onUnauthorized, pushToast]
  );

  /** 問題一覧を取得する */
  const refreshQuestions = useCallback(async (): Promise<void> => {
    try {
      const res = await api.questions();
      setQuestions(res.questions);
      setFinalLocked(res.finalLocked);

      // サーバーが返したクールダウンを反映する
      const now = Date.now();
      setCooldowns((prev) => {
        const next = { ...prev };
        for (const q of res.questions) {
          if (q.cooldownRemainingSec > 0) {
            next[q.id] = now + q.cooldownRemainingSec * 1000;
          }
        }
        return next;
      });
    } catch (err) {
      handleApiError(err);
    }
  }, [handleApiError]);

  /** 得点と開催段階を取得する */
  const refreshScore = useCallback(async (): Promise<void> => {
    try {
      const res = await api.score();
      setScore(res.score);
      setEvent((prev) => (prev.stage === res.stage ? prev : { ...prev, stage: res.stage }));
    } catch (err) {
      if (err instanceof ApiError && err.isUnauthorized) onUnauthorized();
    }
  }, [onUnauthorized]);

  // ------------------------------------------------------------
  // リアルタイム配信の受信
  // ------------------------------------------------------------
  const { connected, browserOnline, resync } = useRealtime(true, {
    onScore(payload) {
      setScore(toScoreView(payload));
    },

    onActivity(payload) {
      // 自分の行動は解答時に通知済みなので、二重に出さない
      if (payload.participantId === participant.id) return;
      const text = activityMessage(payload);
      if (text !== null) {
        pushToast(payload.kind === "correct" ? "correct" : "info", text);
      }
      /*
       * チーム単位の上限では、メンバーの誤答で自分の残り回数も減る。
       * 一覧を取り直すのは正解のときだけなので、ここで手元の値を合わせておく。
       * そうしないと、残り0になった問題に解答欄が開いたままになり、
       * 送ってから初めて上限だと知らされる。
       */
      const left = payload.remainingAttempts;
      if (left !== undefined) {
        setQuestions((prev) =>
          prev === null
            ? prev
            : prev.map((q) =>
                q.id === payload.questionId ? { ...q, remainingAttempts: left } : q
              )
        );
      }
      // メンバーが正解すると解答欄を閉じる必要があるため、一覧を取り直す
      if (payload.kind === "correct") void refreshQuestions();
    },

    onQuestionUnlocked(payload) {
      /*
       * 大謎の解放だけは文言を分ける。順次解放の「次の問題」と同じ調子で流すと、
       * 最後の1問に到達したことが伝わらない（定義書 I011）。
       */
      pushToast(
        "info",
        payload.kind === "final" ? "最後の謎が解放されました" : `${payload.label} が解放されました`
      );
      void refreshQuestions();
    },

    // 開催段階の変更。受付待機・ゲーム・一時停止・結果の切り替えはここが起点になる。
    // 画面は差し替わるが接続は保ったままにするため、この画面自体は外さない。
    // 外すとソケットが切れ、再開の配信を受け取れなくなる。
    onEventStage(payload) {
      setEvent((prev) => ({
        ...prev,
        stage: payload.stage,
        // 段階の変更にあわせて残り時間も届く（延長・再開の直後を正しく出すため）
        ...(payload.remainingSec === undefined ? {} : { remainingSec: payload.remainingSec }),
      }));
      // ゲームが始まった／再開したときは、待っている間の変化を取り込む
      if (payload.stage === "open") void refreshQuestions();
    },

    // 残り時間の補正。画面は自前で数えているため、ここで正しい値へ寄せる
    onTimeTick(payload) {
      setEvent((prev) => ({ ...prev, remainingSec: payload.remainingSec }));
    },

    onSyncFull(payload) {
      setScore(toScoreView(payload.score));
      setEvent((prev) => ({
        ...prev,
        stage: payload.stage,
        remainingSec: payload.remainingSec,
      }));
      // 切断中に問題の解放状況が変わっている可能性がある
      void refreshQuestions();
    },
  });

  // 初回読み込み
  useEffect(() => {
    void refreshQuestions();
  }, [refreshQuestions]);

  /**
   * 接続が切れている間だけ、定期的に取得し直す。
   * WebSocket が使えない環境でも得点が止まらないようにするための保険。
   */
  useEffect(() => {
    if (connected) return;
    const timer = setInterval(() => void refreshScore(), FALLBACK_POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [connected, refreshScore]);

  // 画面復帰時は再同期を要求する
  useEffect(() => {
    const onVisible = (): void => {
      if (document.visibilityState !== "visible") return;
      resync();
      void refreshQuestions();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", onVisible);
    };
  }, [resync, refreshQuestions]);

  /**
   * オフライン表示の判定。
   *
   * ブラウザが「ネットワークなし」と言っている場合は確実なので即座に表示する。
   * 一方、Socket.IO の切断だけの場合は再接続で戻ることが多いため、
   * 少し待ってから表示して点滅を防ぐ。
   */
  const [showOffline, setShowOffline] = useState(false);
  useEffect(() => {
    if (!browserOnline) {
      setShowOffline(true);
      return;
    }
    if (connected) {
      setShowOffline(false);
      return;
    }
    const timer = setTimeout(() => setShowOffline(true), OFFLINE_DISPLAY_DELAY_MS);
    return () => clearTimeout(timer);
  }, [connected, browserOnline]);

  // クールダウン表示のための毎秒更新
  const hasCooldown = Object.values(cooldowns).some((at) => at > Date.now());
  useEffect(() => {
    if (!hasCooldown) return;
    const timer = setInterval(() => forceTick((n) => n + 1), 1000);
    return () => clearInterval(timer);
  }, [hasCooldown]);

  const cooldownSecFor = useCallback(
    (questionId: string): number => {
      const until = cooldowns[questionId];
      if (until === undefined) return 0;
      return Math.max(0, Math.ceil((until - Date.now()) / 1000));
    },
    [cooldowns]
  );

  // ------------------------------------------------------------
  // 解答送信
  // ------------------------------------------------------------
  const submitAnswer = useCallback(
    async (question: QuestionView, submission: AnswerSubmission): Promise<void> => {
      if (submittingId !== null) return;

      const signature = answerSignature(submission);
      // 同じ内容の再送には同じ冪等キーを使い、二重加点・二重減点を防ぐ
      const idempotencyKey = idempotencyKeyFor(question.id, signature);

      setSubmittingId(question.id);
      try {
        const res = await api.submitAnswer({
          questionId: question.id,
          ...submission,
          idempotencyKey,
        });
        releaseIdempotencyKey(question.id, signature);
        setScore(res.score);

        if (res.isCorrect) {
          pushToast("correct", res.message.text);
          setExpandedId(null);
        } else {
          pushToast("wrong", res.message.text);
          if (event.wrongCooldownSec > 0) {
            setCooldowns((prev) => ({
              ...prev,
              [question.id]: Date.now() + event.wrongCooldownSec * 1000,
            }));
          }
        }
        await refreshQuestions();
      } catch (err) {
        handleApiError(err, question.id);
        // 拒否された場合は状態がずれている可能性があるため取得し直す
        if (err instanceof ApiError && !err.isNetworkError) {
          await refreshQuestions();
        }
      } finally {
        setSubmittingId(null);
      }
    },
    [submittingId, event.wrongCooldownSec, pushToast, refreshQuestions, handleApiError]
  );

  /**
   * 解答ボタンからの入口。
   * 残り1回のときだけ確認をはさみ、それ以外はそのまま送る。
   */
  const requestSubmit = useCallback(
    (question: QuestionView, submission: AnswerSubmission): void => {
      if (question.remainingAttempts === 1) {
        setPendingAnswer({ question, submission });
        return;
      }
      void submitAnswer(question, submission);
    },
    [submitAnswer]
  );

  const confirmPendingAnswer = useCallback((): void => {
    const pending = pendingAnswer;
    if (pending === null) return;
    setPendingAnswer(null);
    void submitAnswer(pending.question, pending.submission);
  }, [pendingAnswer, submitAnswer]);

  // ------------------------------------------------------------
  // ヒント開封
  // ------------------------------------------------------------
  const confirmOpenHint = useCallback(async (): Promise<void> => {
    const hint = pendingHint;
    if (hint === null) return;

    setOpeningHintId(hint.id);
    try {
      const res = await api.openHint(hint.id);
      setScore(res.score);
      setPendingHint(null);
      if (res.message !== null) pushToast("info", res.message.text);
      await refreshQuestions();
    } catch (err) {
      handleApiError(err);
      setPendingHint(null);
    } finally {
      setOpeningHintId(null);
    }
  }, [pendingHint, pushToast, refreshQuestions, handleApiError]);

  const locked = event.stage !== "open";
  const ownerName =
    participant.team !== null ? participant.team.name : (participant.displayName ?? "");

  const visibleQuestions = useMemo(() => questions ?? [], [questions]);

  // 開催段階に応じて表示を差し替える。
  //
  // ここで早期 return しても、この画面自体は取り外されないため
  // WebSocket の接続は保たれる。接続が切れると、再開やゲーム開始の配信を
  // 受け取れず、参加者が待機画面のまま取り残される。
  if (event.stage === "reception") {
    return (
      <WaitingScreen
        event={event}
        displayName={participant.displayName}
        teamName={participant.team?.name ?? null}
      />
    );
  }
  if (event.stage === "paused") {
    return <PausedScreen event={event} score={score} ownerName={ownerName} />;
  }
  if (event.stage === "finished" || event.stage === "closed") {
    return <FinishedScreen event={event} score={score} ownerName={ownerName} />;
  }

  return (
    <div className="app">
      <Header
        event={event}
        participant={participant}
        score={score}
        online={!showOffline}
      />

      <main className="main">
        {questions === null ? (
          <p className="main__loading">問題を読み込んでいます…</p>
        ) : visibleQuestions.length === 0 && !finalLocked ? (
          <p className="main__empty">
            表示できる問題がありません。運営スタッフにお声がけください。
          </p>
        ) : (
          <>
            {event.progression === "sequential" && (
              <p className="main__note">正解すると次の問題が表示されます。</p>
            )}
            {visibleQuestions.map((question) => (
              <QuestionCard
                key={question.id}
                question={question}
                expanded={expandedId === question.id}
                submitting={submittingId === question.id}
                openingHintId={openingHintId}
                cooldownSec={cooldownSecFor(question.id)}
                locked={locked}
                onToggle={() =>
                  setExpandedId((prev) => (prev === question.id ? null : question.id))
                }
                onSubmit={(submission) => requestSubmit(question, submission)}
                onRequestHint={(hint) => setPendingHint(hint)}
              />
            ))}
            {/* 未解放の大謎。常に一覧の末尾に置く */}
            {finalLocked && <LockedFinalCard />}
          </>
        )}

        <p className="main__owner-note">
          {ownerName !== "" && `${ownerName} の得点として集計されています`}
        </p>
      </main>

      <ConfirmDialog
        open={pendingHint !== null}
        title={`ヒント${pendingHint?.seq ?? ""} を開きますか？`}
        body={
          pendingHint === null
            ? ""
            : pendingHint.penalty > 0
              ? `このヒントを開封すると ${pendingHint.penalty}点 減点されます。一度開くと取り消せません。`
              : "このヒントは減点なしで開封できます。"
        }
        confirmLabel={
          pendingHint !== null && pendingHint.penalty > 0
            ? `${formatDelta(-pendingHint.penalty)}点で開く`
            : "開く"
        }
        busy={openingHintId !== null}
        onConfirm={() => void confirmOpenHint()}
        onCancel={() => setPendingHint(null)}
      />

      <ConfirmDialog
        open={pendingAnswer !== null}
        title="最後の1回です"
        body={
          pendingAnswer === null
            ? ""
            : pendingAnswer.question.attemptScope === "team"
              ? `${pendingAnswer.question.label} は、この解答でチームの回数を使い切ります。この内容で送信しますか？`
              : `${pendingAnswer.question.label} は、この解答であなたの回数を使い切ります。この内容で送信しますか？`
        }
        confirmLabel="この内容で送信する"
        cancelLabel="戻って見直す"
        onConfirm={confirmPendingAnswer}
        onCancel={() => setPendingAnswer(null)}
      />

      <ToastStack toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
}
